import * as React from "react";
import {
  Box,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Chip,
  Avatar,
  Grid,
  Stack,
  Divider,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
  Tooltip,
  Card,
  CardContent,
  CardMedia,
  ImageList,
  ImageListItem,
  ButtonGroup,
} from "@mui/material";
import { useState, useMemo, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import moment from "moment";

// Icons
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import PersonIcon from "@mui/icons-material/Person";
import FamilyRestroomIcon from "@mui/icons-material/FamilyRestroom";
import WorkIcon from "@mui/icons-material/Work";
import HomeIcon from "@mui/icons-material/Home";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import VisibilityIcon from "@mui/icons-material/Visibility";
import BadgeIcon from "@mui/icons-material/Badge";
import PetsIcon from "@mui/icons-material/Pets";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import LanguageIcon from "@mui/icons-material/Language";
import ReceiptIcon from "@mui/icons-material/Receipt";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ArticleIcon from "@mui/icons-material/Article";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import GavelIcon from "@mui/icons-material/Gavel";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";

import Wrapper from "../../../components/Wrapper";
import title from "../../../components/title";
import { GetAllTenant } from "../../../redux/reducer/GetAllTenant";
import { GetLandlordInterestRequests } from "../../../redux/reducer/FlatInterestRequest";
import RecordNotFound from "../../../components/RecordNotFound";
import ViolationDialog from "../../../components/ViolationDialog";
import PropertyConditionDialog from "../../../components/PropertyConditionDialog";
import "./newRequest.css";

export default function MyTenants() {
  title("My Tenants");
  const open = useSelector((state) => state.sidebar.open);
  const location = useLocation();
  const [mylocation] = useState(location.pathname);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const { validateUser } = useSelector((state) => state);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get initial values from URL params
  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");
  const [filterStatus, setFilterStatus] = useState(searchParams.get("status") || "all");
  const [filterProperty, setFilterProperty] = useState(searchParams.get("property") || "all");
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [violationDialog, setViolationDialog] = useState({
    open: false,
    tenant: null,
  });
  const [conditionDialog, setConditionDialog] = useState({
    open: false,
    tenant: null,
  });

  // Update URL params whenever filters change
  useEffect(() => {
    const params = {};
    if (searchQuery) params.search = searchQuery;
    if (filterStatus && filterStatus !== "all") params.status = filterStatus;
    if (filterProperty && filterProperty !== "all") params.property = filterProperty;
    
    setSearchParams(params);
  }, [searchQuery, filterStatus, filterProperty, setSearchParams]);

  React.useEffect(() => {
    setLoading(true);
    dispatch(GetAllTenant({ userId: localStorage.getItem("user_id") })).then(
      (res) => {
        if (res?.payload?.data?.message === "Get My Tenants Successfully") {
          sessionStorage.setItem(
            "tenant_id",
            res?.payload?.data?.data?.data[0]?._id
          );
          setData(res?.payload?.data?.data?.data || []);
        }
        setLoading(false);
      }
    );
  }, [dispatch, validateUser]);

  // Rented out, paid, but not physically moved in yet. This is a separate
  // dataset from `data` above — GetAllTenant only ever returns tenants who
  // already have an active tenancy (propertyDetail populated, which only
  // happens post move-in via activateTenantForFlat), so a property that's
  // booked and paid for but still "awaiting" move-in never showed up
  // anywhere on this page, and the whole "My Tenants" sidebar link was
  // hidden until move-in — leaving the owner with no confirmation their
  // property was rented out at all in the meantime.
  const [awaitingMoveIns, setAwaitingMoveIns] = useState([]);
  const [awaitingLoading, setAwaitingLoading] = useState(false);

  useEffect(() => {
    const landlordId =
      localStorage.getItem("user_id") || sessionStorage.getItem("user_id");
    if (!landlordId) return;

    setAwaitingLoading(true);
    dispatch(GetLandlordInterestRequests({ landlordId }))
      .then((res) => {
        const requests =
          res?.payload?.data?.data || res?.payload?.data || [];
        setAwaitingMoveIns(
          (Array.isArray(requests) ? requests : []).filter(
            (r) => r.moveInStatus === "awaiting"
          )
        );
      })
      .finally(() => setAwaitingLoading(false));
  }, [dispatch, validateUser]);

  // Filtered & Searched Data
  const filteredData = useMemo(() => {
    if (!data || data.length === 0) return [];

    return data.filter((tenant) => {
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch =
        !searchQuery ||
        tenant.name?.toLowerCase().includes(searchLower) ||
        tenant.cnicNo?.toLowerCase().includes(searchLower) ||
        tenant.fatherName?.toLowerCase().includes(searchLower) ||
        tenant.propertyDetail?.[0]?.flatName?.toLowerCase().includes(searchLower);

      const matchesStatus =
        filterStatus === "all" ||
        (filterStatus === "active" && tenant.is_active) ||
        (filterStatus === "inactive" && !tenant.is_active) ||
        (filterStatus === "accepted" && tenant.is_accept) ||
        (filterStatus === "rejected" && tenant.is_reject);

      const matchesProperty =
        filterProperty === "all" ||
        tenant.propertyDetail?.[0]?.flatName === filterProperty;

      return matchesSearch && matchesStatus && matchesProperty;
    });
  }, [data, searchQuery, filterStatus, filterProperty]);

  const uniqueProperties = useMemo(() => {
    const properties = new Set();
    data.forEach((tenant) => {
      if (tenant.propertyDetail?.[0]?.flatName) {
        properties.add(tenant.propertyDetail[0].flatName);
      }
    });
    return Array.from(properties);
  }, [data]);

  const handleViewDetails = (tenant) => {
    setSelectedTenant(tenant);
    setDetailModalOpen(true);
  };

  const handleCloseModal = () => {
    setDetailModalOpen(false);
    setSelectedTenant(null);
  };

  const handleReportViolation = (tenant) => {
    setViolationDialog({ open: true, tenant });
  };

  const handlePropertyCondition = (tenant) => {
    setConditionDialog({ open: true, tenant });
  };

  const handleManageTenant = (tenant) => {
    const flatId = tenant?.propertyDetail?.[0]?._id;

    // No flat on the tenant's record means no tenancy to manage — the old code
    // interpolated `undefined` straight into the path and opened a broken page.
    if (!flatId) {
      toast.info(
        `${tenant?.name || "This tenant"} has booked a property but has not moved in yet. Confirm the move-in from Interest Requests first.`
      );
      return;
    }

    navigate(`/landlord-functionality/${tenant?.userId}/${flatId}`, {
      state: {
        tenantName: tenant?.name,
        from: "/my-tenants",
      },
    });
  };

  function TenantRow({ row, index }) {
    const [open, setOpen] = React.useState(false);

    return (
      <React.Fragment>
        <TableRow
          sx={{
            "& > *": { borderBottom: "unset" },
            backgroundColor: open ? "rgba(103, 58, 183, 0.05)" : "transparent",
            transition: "all 0.3s ease",
            "&:hover": {
              backgroundColor: "rgba(103, 58, 183, 0.08)",
            },
          }}
        >
          <TableCell>
            <IconButton
              size="small"
              onClick={() => setOpen(!open)}
              sx={{
                transition: "transform 0.3s ease",
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
              }}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </TableCell>
          <TableCell>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Avatar
                src={row.profilePhoto}
                sx={{
                  width: 45,
                  height: 45,
                  bgcolor: "primary.main",
                  fontWeight: 700,
                }}
              >
                {row.name?.charAt(0)?.toUpperCase()}
              </Avatar>
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {row.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {row.gender} • {row.tenantType}
                </Typography>
              </Box>
            </Box>
          </TableCell>
          <TableCell>
            <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 500 }}>
              {row.cnicNo}
            </Typography>
          </TableCell>
          <TableCell>
            <Chip
              label={row.propertyDetail[0]?.flatName}
              size="small"
              sx={{
                fontWeight: 600,
                backgroundColor: "rgba(33, 150, 243, 0.1)",
                color: "primary.main",
              }}
            />
            <Typography variant="caption" display="block" color="text.secondary">
              #{row.propertyDetail[0]?.flatNumber} • Floor {row.propertyDetail[0]?.flatFloor}
            </Typography>
          </TableCell>
          <TableCell align="right">
            <Typography variant="body2" sx={{ fontWeight: 700, color: "success.main" }}>
              Rs. {parseInt(row.propertyDetail[0]?.flatRent).toLocaleString()}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Advance: Rs. {parseInt(row.propertyDetail[0]?.flatAdvance).toLocaleString()}
            </Typography>
          </TableCell>
          <TableCell align="center">
            <Stack direction="row" spacing={0.5} justifyContent="center">
              {row.is_active && (
                <Chip
                  label="Active"
                  size="small"
                  color="success"
                  icon={<CheckCircleIcon />}
                  sx={{ fontWeight: 600 }}
                />
              )}
              {row.is_accept && (
                <Chip
                  label="Accepted"
                  size="small"
                  color="primary"
                  sx={{ fontWeight: 600 }}
                />
              )}
            </Stack>
          </TableCell>
          <TableCell align="center">
            <Stack direction="row" spacing={1} justifyContent="center">
              <Tooltip title="View Full Details">
                <IconButton
                  size="small"
                  onClick={() => handleViewDetails(row)}
                  sx={{
                    backgroundColor: "rgba(33, 150, 243, 0.1)",
                    "&:hover": {
                      backgroundColor: "rgba(33, 150, 243, 0.2)",
                    },
                  }}
                >
                  <VisibilityIcon fontSize="small" color="primary" />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Manage Tenant (Agreement, Bills, Notices)">
                <IconButton
                  size="small"
                  onClick={() => handleManageTenant(row)}
                  sx={{
                    background: "linear-gradient(135deg, var(--owner-primary) 0%, var(--owner-nav) 100%)",
                    color: "white",
                    "&:hover": {
                      background: "linear-gradient(135deg, var(--owner-nav) 0%, var(--owner-primary) 100%)",
                    },
                  }}
                >
                  <ManageAccountsIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Report Agreement Violation">
                <IconButton
                  size="small"
                  onClick={() => handleReportViolation(row)}
                  sx={{
                    backgroundColor: "rgba(211, 47, 47, 0.1)",
                    "&:hover": {
                      backgroundColor: "rgba(211, 47, 47, 0.2)",
                    },
                  }}
                >
                  <WarningAmberIcon fontSize="small" color="error" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Property Condition (before/after, damage claim)">
                <IconButton
                  size="small"
                  onClick={() => handlePropertyCondition(row)}
                  sx={{
                    backgroundColor: "rgba(8, 145, 178, 0.1)",
                    "&:hover": {
                      backgroundColor: "rgba(8, 145, 178, 0.2)",
                    },
                  }}
                >
                  <PhotoCameraIcon fontSize="small" sx={{ color: "#0891b2" }} />
                </IconButton>
              </Tooltip>
            </Stack>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <Card elevation={0} sx={{ backgroundColor: "rgba(33, 150, 243, 0.05)" }}>
                      <CardContent>
                        <Stack spacing={1}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <PhoneIcon sx={{ color: "primary.main" }} />
                            <Typography variant="caption" fontWeight={600}>Contact</Typography>
                          </Box>
                          <Typography variant="body2">{row.mobileNumber}</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                            {row.email}
                          </Typography>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card elevation={0} sx={{ backgroundColor: "rgba(76, 175, 80, 0.05)" }}>
                      <CardContent>
                        <Stack spacing={1}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <FamilyRestroomIcon sx={{ color: "success.main" }} />
                            <Typography variant="caption" fontWeight={600}>Family</Typography>
                          </Box>
                          <Typography variant="h6" fontWeight={700} color="success.main">
                            {row.totalFamilyMembers} Members
                          </Typography>
                          <Typography variant="caption">{row.maritalStatus}</Typography>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card elevation={0} sx={{ backgroundColor: "rgba(255, 152, 0, 0.05)" }}>
                      <CardContent>
                        <Stack spacing={1}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <WorkIcon sx={{ color: "warning.main" }} />
                            <Typography variant="caption" fontWeight={600}>Occupation</Typography>
                          </Box>
                          <Typography variant="body2" textTransform="capitalize">
                            {row.employmentStatus}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.companyName || row.instituteName}
                          </Typography>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card elevation={0} sx={{ backgroundColor: "rgba(103, 58, 183, 0.05)" }}>
                      <CardContent>
                        <Stack spacing={1}>
                          <Typography variant="caption" fontWeight={600} color="text.secondary">
                            Quick Actions
                          </Typography>
                          <Button
                            fullWidth
                            size="small"
                            variant="contained"
                            startIcon={<ManageAccountsIcon />}
                            onClick={() => handleManageTenant(row)}
                            sx={{
                              background: "linear-gradient(135deg, var(--owner-primary) 0%, var(--owner-nav) 100%)",
                              textTransform: "none",
                              fontWeight: 600,
                              fontSize: "0.75rem",
                              "&:hover": {
                                background: "linear-gradient(135deg, var(--owner-nav) 0%, var(--owner-primary) 100%)",
                              },
                            }}
                          >
                            Manage
                          </Button>
                          <Stack direction="row" spacing={1}>
                            <Tooltip title="Agreement">
                              <IconButton size="small" sx={{ backgroundColor: "rgba(103, 58, 183, 0.1)" }}>
                                <GavelIcon fontSize="small" color="secondary" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Bills">
                              <IconButton size="small" sx={{ backgroundColor: "rgba(76, 175, 80, 0.1)" }}>
                                <ReceiptIcon fontSize="small" color="success" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Report Agreement Violation">
                              <IconButton
                                size="small"
                                onClick={() => handleReportViolation(row)}
                                sx={{ backgroundColor: "rgba(255, 152, 0, 0.1)" }}
                              >
                                <NotificationsIcon fontSize="small" color="warning" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Property Condition (before/after, damage claim)">
                              <IconButton
                                size="small"
                                onClick={() => handlePropertyCondition(row)}
                                sx={{ backgroundColor: "rgba(8, 145, 178, 0.1)" }}
                              >
                                <PhotoCameraIcon fontSize="small" sx={{ color: "#0891b2" }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  }

  return (
    <>
      <Wrapper open={open} mylocation={mylocation} />
      <div className={`${open ? "sidebar-open" : "sidebar-closed"}`}>
        {/* Header */}
        <Box
          sx={{
            background: "linear-gradient(135deg, var(--owner-primary) 0%, var(--owner-nav) 100%)",
            borderRadius: 3,
            p: 4,
            mb: 3,
            boxShadow: "0 18px 38px -22px rgba(18,32,58,0.6)",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
            <Box>
              <Typography variant="h3" sx={{ color: "white", fontWeight: 800, fontSize: { xs: "2rem", md: "2.5rem" } }}>
                My Tenants
              </Typography>
              <Typography variant="body1" sx={{ color: "rgba(255, 255, 255, 0.9)" }}>
                Manage agreements, bills, and notices
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {data.length > 0 && (
                <Chip
                  icon={<PersonIcon sx={{ color: "white !important" }} />}
                  label={`${data.length} Active ${data.length === 1 ? "Tenant" : "Tenants"}`}
                  sx={{ backgroundColor: "rgba(255, 255, 255, 0.2)", color: "white", fontWeight: 700 }}
                />
              )}
              {awaitingMoveIns.length > 0 && (
                <Chip
                  icon={<HourglassEmptyIcon sx={{ color: "white !important" }} />}
                  label={`${awaitingMoveIns.length} Awaiting Move-In`}
                  sx={{ backgroundColor: "rgba(255, 152, 0, 0.55)", color: "white", fontWeight: 700 }}
                />
              )}
            </Stack>
          </Box>
        </Box>

        {/* Rented out, awaiting move-in — booked and paid, but the tenant
            hasn't physically moved in yet. Read-only summary + a link to the
            actual confirm action on New Request; deliberately not agreement
            /bills/rent-collection, since none of that applies to a tenancy
            that hasn't started. One card per accepted request, so several
            tenants across different properties each get their own card. */}
        {!awaitingLoading && awaitingMoveIns.length > 0 && (
          <Paper
            elevation={2}
            sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 3, borderLeft: "4px solid #ff9800" }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
                mb: 0.5,
              }}
            >
              <Typography variant="h6" fontWeight={700}>
                Rented Out — Awaiting Move-In
              </Typography>
              <Chip
                size="small"
                icon={<HourglassEmptyIcon />}
                label={`${awaitingMoveIns.length} ${awaitingMoveIns.length === 1 ? "tenant" : "tenants"} waiting`}
                color="warning"
                variant="outlined"
                sx={{ fontWeight: 700 }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              These properties are booked and paid for, but the tenant hasn't moved in yet.
              Agreement, bills and rent collection open up once move-in is confirmed.
            </Typography>
            <Grid container spacing={2.5}>
              {awaitingMoveIns.map((request) => {
                const moveInDate = moment(request.moveInDate, "DD-MM-YYYY", true);
                const isOverdue =
                  moveInDate.isValid() && moveInDate.isBefore(moment(), "day");
                const tenantSelfReported = Boolean(request.tenantMarkedMoveInAt);
                const accent = tenantSelfReported
                  ? "#2e7d32"
                  : isOverdue
                    ? "#ed6c02"
                    : "#9e9e9e";
                const photo =
                  request.tenantDetail?.profilePhoto ||
                  request.tenantProfile ||
                  request.tenantImage;

                return (
                  <Grid item xs={12} md={6} xl={4} key={request._id || request.id}>
                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: 3,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        borderTop: `4px solid ${accent}`,
                        transition: "box-shadow 0.2s ease, transform 0.2s ease",
                        "&:hover": {
                          boxShadow: "0 10px 24px -12px rgba(0,0,0,0.25)",
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Avatar
                            src={photo}
                            sx={{ width: 48, height: 48, bgcolor: accent, fontWeight: 700 }}
                          >
                            {request.tenantName?.charAt(0)?.toUpperCase()}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography fontWeight={700} noWrap>
                              {request.tenantName || "Unknown Tenant"}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              noWrap
                              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                            >
                              <HomeIcon sx={{ fontSize: 16 }} />
                              {request.flatName || "Property"}
                            </Typography>
                            {(request.flatDetail?.flatNumber || request.flatDetail?.flatFloor) && (
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {request.flatDetail?.flatNumber
                                  ? `Flat #${request.flatDetail.flatNumber}`
                                  : ""}
                                {request.flatDetail?.flatNumber && request.flatDetail?.flatFloor
                                  ? " • "
                                  : ""}
                                {request.flatDetail?.flatFloor
                                  ? `Floor ${request.flatDetail.flatFloor}`
                                  : ""}
                              </Typography>
                            )}
                          </Box>
                        </Box>

                        <Chip
                          size="small"
                          icon={tenantSelfReported ? <EventAvailableIcon /> : <HourglassEmptyIcon />}
                          label={
                            tenantSelfReported
                              ? "Tenant says they moved in"
                              : isOverdue
                                ? "Move-in date passed"
                                : "Not moved in yet"
                          }
                          color={tenantSelfReported ? "success" : isOverdue ? "warning" : "default"}
                          sx={{ alignSelf: "flex-start", mt: 1.5, fontWeight: 600 }}
                        />

                        <Divider sx={{ my: 1.5 }} />

                        <Stack spacing={0.75}>
                          <Typography variant="body2" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <CalendarTodayIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                            <span>
                              <strong>Move-in:</strong> {request.moveInDate || "N/A"}
                            </span>
                          </Typography>
                          {request.flatRent && (
                            <Typography variant="body2" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <AttachMoneyIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                              <span>
                                <strong>Rent:</strong> {request.flatRent}
                              </span>
                            </Typography>
                          )}
                          {request.tenantPhone && (
                            <Typography variant="body2" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <PhoneIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                              <span>03{request.tenantPhone}</span>
                            </Typography>
                          )}
                        </Stack>

                        {isOverdue && !tenantSelfReported && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
                            We're reminding the tenant automatically until they confirm, or this
                            auto-confirms a few days after the move-in date in case they simply
                            forgot to tell us.
                          </Typography>
                        )}

                        <Box sx={{ flexGrow: 1 }} />

                        <Button
                          fullWidth
                          size="small"
                          variant={tenantSelfReported ? "contained" : "outlined"}
                          sx={{ mt: 2, textTransform: "none", fontWeight: 600 }}
                          onClick={() => navigate("/new-request")}
                        >
                          {tenantSelfReported ? "Review & Confirm Move-in" : "View in New Request"}
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        )}

        {/* Filters & Search */}
        {!loading && data.length > 0 && (
        <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                placeholder="Search by name, CNIC, property..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  label="Status"
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="accepted">Accepted</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel>Property</InputLabel>
                <Select
                  value={filterProperty}
                  label="Property"
                  onChange={(e) => setFilterProperty(e.target.value)}
                >
                  <MenuItem value="all">All Properties</MenuItem>
                  {uniqueProperties.map((prop) => (
                    <MenuItem key={prop} value={prop}>
                      {prop}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          
          {/* Active Filters Display */}
          {(searchQuery || filterStatus !== "all" || filterProperty !== "all") && (
            <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary">
                Active Filters:
              </Typography>
              {searchQuery && (
                <Chip
                  label={`Search: "${searchQuery}"`}
                  size="small"
                  onDelete={() => setSearchQuery("")}
                  color="primary"
                  variant="outlined"
                />
              )}
              {filterStatus !== "all" && (
                <Chip
                  label={`Status: ${filterStatus}`}
                  size="small"
                  onDelete={() => setFilterStatus("all")}
                  color="primary"
                  variant="outlined"
                />
              )}
              {filterProperty !== "all" && (
                <Chip
                  label={`Property: ${filterProperty}`}
                  size="small"
                  onDelete={() => setFilterProperty("all")}
                  color="primary"
                  variant="outlined"
                />
              )}
              <Button
                size="small"
                onClick={() => {
                  setSearchQuery("");
                  setFilterStatus("all");
                  setFilterProperty("all");
                }}
                sx={{ ml: 1 }}
              >
                Clear All
              </Button>
            </Box>
          )}
        </Paper>
        )}

        {/* Table */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", m: 10 }}>
            <CircularProgress size={60} />
          </Box>
        ) : filteredData?.length > 0 ? (
          <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 3 }}>
            <Table>
              <TableHead sx={{ background: "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)" }}>
                <TableRow>
                  <TableCell sx={{ width: 60 }} />
                  <TableCell sx={{ color: "white", fontWeight: 700 }}>Tenant Info</TableCell>
                  <TableCell sx={{ color: "white", fontWeight: 700 }}>CNIC</TableCell>
                  <TableCell sx={{ color: "white", fontWeight: 700 }}>Property</TableCell>
                  <TableCell sx={{ color: "white", fontWeight: 700 }} align="right">Rent</TableCell>
                  <TableCell sx={{ color: "white", fontWeight: 700 }} align="center">Status</TableCell>
                  <TableCell sx={{ color: "white", fontWeight: 700 }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.map((row, index) => (
                  <TenantRow key={row._id} row={row} index={index} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : awaitingLoading || awaitingMoveIns.length > 0 ? null : (
          <Box sx={{ display: "flex", justifyContent: "center", m: 10 }}>
            <RecordNotFound />
          </Box>
        )}

        {/* Detail Modal */}
        <TenantDetailModal
          open={detailModalOpen}
          tenant={selectedTenant}
          onClose={handleCloseModal}
          onManage={handleManageTenant}
        />

        {/* Violation Dialog */}
        <ViolationDialog
          open={violationDialog.open}
          onClose={() => setViolationDialog({ open: false, tenant: null })}
          tenantId={violationDialog.tenant?.userId}
          propertyId={violationDialog.tenant?.propertyDetail?.[0]?._id}
          tenantName={violationDialog.tenant?.name}
          flatName={violationDialog.tenant?.propertyDetail?.[0]?.flatName}
        />

        {/* Property Condition Dialog */}
        <PropertyConditionDialog
          open={conditionDialog.open}
          onClose={() => setConditionDialog({ open: false, tenant: null })}
          tenantId={conditionDialog.tenant?.userId}
          flatId={conditionDialog.tenant?.propertyDetail?.[0]?._id}
          tenantName={conditionDialog.tenant?.name}
          flatName={conditionDialog.tenant?.propertyDetail?.[0]?.flatName}
          landlordId={localStorage.getItem("user_id") || sessionStorage.getItem("user_id")}
        />

        <ToastContainer
          position="top-right"
          autoClose={4000}
          newestOnTop
          closeOnClick
          pauseOnHover
          theme="colored"
        />
      </div>
    </>
  );
}

// Tenant Detail Modal Component
function TenantDetailModal({ open, tenant, onClose, onManage }) {
  if (!tenant) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ background: "linear-gradient(135deg, var(--owner-primary) 0%, var(--owner-nav) 100%)", color: "white", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar src={tenant.profilePhoto} sx={{ width: 50, height: 50, border: "3px solid white" }}>
            {tenant.name?.charAt(0)}
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={700}>{tenant.name}</Typography>
            <Typography variant="caption">{tenant.email}</Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "white" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        <Grid container spacing={3}>
          {/* Personal Information */}
          <Grid item xs={12}>
            <Typography variant="h6" fontWeight={700} color="primary" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PersonIcon /> Personal Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <InfoCard label="Father Name" value={tenant.fatherName} icon={<PersonIcon />} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <InfoCard label="CNIC" value={tenant.cnicNo} icon={<BadgeIcon />} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <InfoCard label="Mobile" value={tenant.mobileNumber} icon={<PhoneIcon />} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <InfoCard label="Gender" value={tenant.gender} icon={<PersonIcon />} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoCard label="Current Address" value={tenant.currentAddress} icon={<LocationOnIcon />} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoCard label="Permanent Address" value={tenant.permanentAddress} icon={<LocationOnIcon />} />
              </Grid>
            </Grid>
          </Grid>

          {/* CNIC Documents */}
          <Grid item xs={12}>
            <Typography variant="h6" fontWeight={700} color="primary" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <BadgeIcon /> CNIC Documents
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Card>
                  <CardMedia component="img" height="200" image={tenant.cnicFront} alt="CNIC Front" sx={{ objectFit: "contain", p: 2 }} />
                  <CardContent>
                    <Typography variant="caption" fontWeight={600}>CNIC Front</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Card>
                  <CardMedia component="img" height="200" image={tenant.cnicBack} alt="CNIC Back" sx={{ objectFit: "contain", p: 2 }} />
                  <CardContent>
                    <Typography variant="caption" fontWeight={600}>CNIC Back</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>

          {/* Family & Employment */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" fontWeight={700} color="primary" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FamilyRestroomIcon /> Family Details
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={2}>
              <InfoCard label="Marital Status" value={tenant.maritalStatus} />
              <InfoCard label="Total Members" value={tenant.totalFamilyMembers} />
              <InfoCard label="Languages" value={tenant.language?.join(", ")} />
              <InfoCard label="Cast" value={tenant.cast} />
            </Stack>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="h6" fontWeight={700} color="primary" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <WorkIcon /> Employment Details
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={2}>
              <InfoCard label="Employment Status" value={tenant.employmentStatus} />
              <InfoCard label="Company/Institute" value={tenant.companyName || tenant.instituteName} />
              <InfoCard label="Monthly Income" value={tenant.monthlyIncome} />
            </Stack>
          </Grid>

          {/* Additional Info */}
          <Grid item xs={12}>
            <Typography variant="h6" fontWeight={700} color="primary" gutterBottom>
              Additional Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <InfoCard label="Pets" value={tenant.hasPets ? `Yes (${tenant.petType}, ${tenant.petCount})` : "No"} icon={<PetsIcon />} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <InfoCard label="Vehicle" value={tenant.hasVehicle ? `Yes (${tenant.vehicleType})` : "No"} icon={<DirectionsCarIcon />} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <InfoCard label="Move-in Preference" value={tenant.preferredMoveIn} icon={<CalendarTodayIcon />} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <InfoCard label="Budget Range" value={`Rs. ${tenant.budgetMin?.toLocaleString()} - ${tenant.budgetMax?.toLocaleString()}`} icon={<AttachMoneyIcon />} />
              </Grid>
            </Grid>
          </Grid>

          {/* Emergency Contact */}
          <Grid item xs={12}>
            <Typography variant="h6" fontWeight={700} color="primary" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ContactPhoneIcon /> Emergency Contact
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <InfoCard label="Name" value={tenant.emergencyContactName} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <InfoCard label="Relation" value={tenant.emergencyContactRelation} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <InfoCard label="Phone" value={tenant.emergencyContactPhone} />
              </Grid>
            </Grid>
          </Grid>

          {/* About Me */}
          {tenant.aboutMe && (
            <Grid item xs={12}>
              <Typography variant="h6" fontWeight={700} color="primary" gutterBottom>
                About Tenant
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Paper elevation={0} sx={{ p: 2, backgroundColor: "rgba(103, 58, 183, 0.05)" }}>
                <Typography variant="body2">{tenant.aboutMe}</Typography>
              </Paper>
            </Grid>
          )}

          {/* Property Images */}
          {tenant.propertyDetail?.[0]?.images?.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="h6" fontWeight={700} color="primary" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <HomeIcon /> Property Images
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <ImageList cols={4} gap={8}>
                {tenant.propertyDetail[0].images.map((img, idx) => (
                  <ImageListItem key={idx}>
                    <img src={img} alt={`Property ${idx + 1}`} loading="lazy" style={{ borderRadius: 8 }} />
                  </ImageListItem>
                ))}
              </ImageList>
            </Grid>
          )}

          {/* Management Actions Info */}
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 3, backgroundColor: "rgba(103, 58, 183, 0.05)", borderRadius: 2 }}>
              <Typography variant="h6" fontWeight={700} color="secondary" gutterBottom>
                📋 Available Management Actions
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <GavelIcon color="secondary" />
                    <Typography variant="body2" fontWeight={600}>View/Sign Agreement</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ReceiptIcon color="success" />
                    <Typography variant="body2" fontWeight={600}>Upload Bills</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <NotificationsIcon color="warning" />
                    <Typography variant="body2" fontWeight={600}>Send Notices</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: "1px solid rgba(0,0,0,0.1)" }}>
        <Button onClick={onClose} variant="outlined">Close</Button>
        <Button
          variant="contained"
          startIcon={<ManageAccountsIcon />}
          sx={{
            background: "linear-gradient(135deg, var(--owner-primary) 0%, var(--owner-nav) 100%)",
            fontWeight: 600,
            px: 3,
            "&:hover": {
              background: "linear-gradient(135deg, var(--owner-nav) 0%, var(--owner-primary) 100%)",
            },
          }}
          onClick={() => onManage(tenant)}
        >
          Manage Tenant
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Info Card Component
function InfoCard({ label, value, icon }) {
  return (
    <Paper elevation={0} sx={{ p: 2, backgroundColor: "rgba(0,0,0,0.02)", borderRadius: 2 }}>
      <Stack spacing={0.5}>
        {icon && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {React.cloneElement(icon, { sx: { fontSize: 18, color: "primary.main" } })}
            <Typography variant="caption" fontWeight={600} color="text.secondary">
              {label}
            </Typography>
          </Box>
        )}
        {!icon && (
          <Typography variant="caption" fontWeight={600} color="text.secondary">
            {label}
          </Typography>
        )}
        <Typography variant="body2" fontWeight={600} sx={{ textTransform: "capitalize" }}>
          {value || "N/A"}
        </Typography>
      </Stack>
    </Paper>
  );
}